import {useFormik} from 'formik';
import {useEffect, useRef} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {toast} from 'react-toastify';

import Button from '../../../../components/button';
import ChatHeaderIcon from '../../../../components/mazraeKit/headers/ChatHeaderIcon/ChatHeaderIcon';
import CustomHeader from '../../../../components/mazraeKit/headers/homeHeader';
import NotificationHeaderIcon from '../../../../components/mazraeKit/headers/NotificationHeaderIcon/NotificationHeaderIcon';

import usePostSubBrandImage from '../../../../services/hookService/postSubBrandImage/usePostSubBrandImage';
import usePostSubBrandProduct from '../../../../services/hookService/postSubBrandProduct/usePostSubBrandProduct';

import {InputGroupCode} from '../../../../utilities/helper/unitGenerator/unit.types';
import EnginePower from './EnginePower';
import NameInput from './NameInput';
import PriceInput from './PriceInput';
import ProducingCompany from './ProducingCompany';
import ProducingCountry from './ProducingCountry';

import {
  brandProductSchema,
  initialValuesProduct,
  IProductForm,
} from './productFormData';
import ProductImages from './ProductImages';
import SelectFormulation from './SelectFormulation';
import SelectInput from './SelectInput';
import SelectInputGroup from './SelectInputGroup';
import SelectSubInput from './SelectSubInput';
import TitleDescription from './TitleDescription';

import WeightInput from './WeightInput';

const SKIP_UNIT_VALIDATION_GROUPS = [InputGroupCode.MACHINERY];

const AddProduct = () => {
  const navigate = useNavigate();
  const nameInputRef = useRef<HTMLDivElement>(null);
  const {brandId} = useParams();

  const {mutateAsync, imagesLoading} = usePostSubBrandImage();
  const {mutate, isLoading} = usePostSubBrandProduct();

  const form = useFormik<IProductForm>({
    initialValues: initialValuesProduct(+brandId!),
    validationSchema: brandProductSchema,

    validate: values => {
      const errors: any = {};

      const group = Number(values.inputGroupCode);

      const isSkippableGroup =
        group && SKIP_UNIT_VALIDATION_GROUPS.includes(group);

      if (!isSkippableGroup) {
        if (!values.weight) {
          errors.weight = 'وزن را انتخاب کنید';
        }
        if (!values.weightUnit) {
          errors.weightUnit = 'واحد وزن را انتخاب کنید';
        }
      }

      return errors;
    },

    onSubmit: async values => {
      // First handle image uploads if there are any
      let imageIds = [...values.imagesId];
      if (values.imagesFile.length > 0) {
        const uploadPromises = values.imagesFile.map(image => {
          const singleImagePayload = new FormData();
          singleImagePayload.append('image', image);
          return mutateAsync(singleImagePayload);
        });

        const responses = await Promise.all(uploadPromises);
        imageIds = [...imageIds, ...responses.map(res => res.data.id)];
      }

      // Only proceed if we have at least one image (either existing or newly uploaded)
      if (imageIds.length > 0) {
        // Create main form payload
        const payloadToSubmit = {
          brand: values.brandId,
          name: values.name,
          advertisement_title: values.title,
          advertisement_description: values.description,
          ad_price: values.price,
          producing_country: values.producingCountry,
          images: imageIds,
          sub_input_guid: values.subInput,

          additional_data: {
            weight: values.weight,
            weightUnit: values.weightUnit,
            producingCompany: values.producingCompany,
            formulation: values.formulation,
            enginePower: values.enginePower,
            combinations: values.combinations,
            application_method: values.application_method,
          },
        };

        // Submit the product data
        mutate(payloadToSubmit, {
          onSuccess: () => {
            navigate(`/my-store-brands/brand/${values.brandId}`);
          },

          // Show toast error when brand name is repetetive
          onError: error => {
            if (
              (error as any)?.response?.data?.non_field_errors?.[0]?.includes(
                'unique set',
              )
            ) {
              toast.error('نام محصول تکراری است');
            } else {
              toast.error('خطا در ثبت محصول. لطفا دوباره تلاش کنید');
            }
          },
        });
      }
    },
  });

  useEffect(() => {
    if (form.values.inputGuid) {
      nameInputRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [form.values.inputGuid]);

  const allIsLoading = imagesLoading || isLoading;

  return (
    <div className="flex relative flex-col pb-14 w-full h-screen">
      <div>
        <CustomHeader
          canScroll={true}
          title="افزودن محصولات برند"
          preIcon="icon-nameArrow---Right"
          isActiveRightIcon
          onClick={() => navigate(-1)}
          postComponent={() => (
            <div className="flex gap-3">
              <NotificationHeaderIcon />
              <ChatHeaderIcon />
            </div>
          )}
        />
      </div>

      <form
        id="product-form"
        onSubmit={form.handleSubmit}
        className="flex overflow-y-auto flex-col gap-3 p-4 mb-10 w-full"
      >
        <SelectInputGroup form={form} />

        {form.values.inputGroupCode && (
          <>
            <SelectInput form={form} />
            <NameInput form={form} nameInputRef={nameInputRef} />
            <SelectSubInput form={form} />

            <WeightInput form={form} />
            <TitleDescription form={form} />
            <ProductImages form={form} />
            <PriceInput form={form} />
            <ProducingCompany form={form} />
            <ProducingCountry form={form} />

            {form.values.inputGroupCode === InputGroupCode.MACHINERY && (
              <EnginePower form={form} />
            )}

            {(form.values.inputGroupCode === InputGroupCode.FERTILIZER ||
              form.values.inputGroupCode === InputGroupCode.PESTICIDE) && (
              <SelectFormulation form={form} />
            )}
          </>
        )}
      </form>

      {form.values.inputGroupCode && (
        <div className="fixed right-4 left-4 bottom-8">
          <Button
            className="w-full"
            type="submit"
            form="product-form"
            disabled={allIsLoading || !form.isValid}
            isLoading={allIsLoading}
          >
            افزودن محصول
          </Button>
        </div>
      )}
    </div>
  );
};

export default AddProduct;
